import { Chip, Tooltip, Typography, Box } from '@mui/material';
import DrilldownDialog from './DrilldownDialog';
import { KIND_LABEL, leadDetailsUrl } from './leadDetails';
import { Date1, Mobile, Stack2, digits, fmt, money } from './drilldownCells';

// The popup behind a count on LCR / ACR / LTA: the leads that make up the number, with what is needed to act on them.
//   - leads (assigned / assessed): who, mobile number, where from, when it came, when it was assigned, when it was assessed
//   - admissions: student, admission number, course, program, when admitted, what was collected, the lead's mobile, the CRO
// Fast: the answer is usually already remembered (the count prefetches it on hover), the table shows 25 rows at a time,
// search / sort / paging all happen in the browser, and the skeleton keeps the popup from jumping while it loads.
// first admission of the lead (a lead with several admissions is rare - the rest are named in a "+N more" chip)
const sumCollected = (row) => (row.admissions || []).reduce((total, a) => total + (a.collected || 0), 0);
const first = (row) => (row.admissions && row.admissions[0]) || {};

const LEAD_COLUMNS = (kind) => [
  { key: 'name', label: 'Lead', sort: (r) => (r.name || '').toLowerCase(), render: (r) => <Stack2 top={r.name} bottom={r.source} />, csv: (r) => r.name },
  { key: 'mobile', label: 'Mobile', sort: (r) => digits(r.mobile), render: (r) => <Mobile value={r.mobile} />, csv: (r) => r.mobile },
  { key: 'cameOn', label: 'Came in', sort: (r) => r.cameOn || '', render: (r) => <Date1 value={r.cameOn} />, csv: (r) => fmt(r.cameOn) },
  { key: 'assignedOn', label: kind === 'assigned' || kind === 'assessed' ? 'Assigned' : 'Assigned', sort: (r) => r.assignedOn || '', render: (r) => <Date1 value={r.assignedOn} />, csv: (r) => fmt(r.assignedOn) },
  { key: 'assessedOn', label: 'Assessed', sort: (r) => r.assessedOn || '', render: (r) => <Date1 value={r.assessedOn} empty="Not yet" />, csv: (r) => fmt(r.assessedOn) },
];

const ADMISSION_COLUMNS = [
  { key: 'student', label: 'Student', sort: (r) => (first(r).studentName || '').toLowerCase(), render: (r) => <Stack2 top={first(r).studentName} bottom={first(r).studentMobile} />, csv: (r) => first(r).studentName },
  { key: 'admnNo', label: 'Admn no', sort: (r) => String(first(r).admnNo || ''), render: (r) => (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Typography variant="body2" fontWeight={600}>{first(r).admnNo || '-'}</Typography>
      {r.admissions && r.admissions.length > 1 && (
        <Tooltip title={r.admissions.slice(1).map((a) => `${a.admnNo} - ${a.course || ''}`).join('\n')}>
          <Chip size="small" label={`+${r.admissions.length - 1} more`} />
        </Tooltip>
      )}
    </Box>
  ), csv: (r) => (r.admissions || []).map((a) => a.admnNo).join(' / ') },
  { key: 'course', label: 'Course', sort: (r) => (first(r).course || '').toLowerCase(), render: (r) => <Stack2 top={first(r).course} bottom={first(r).program} />, csv: (r) => first(r).course },
  { key: 'admittedOn', label: 'Admitted', sort: (r) => first(r).admittedOn || '', render: (r) => <Date1 value={first(r).admittedOn} />, csv: (r) => fmt(first(r).admittedOn) },
  { key: 'collected', label: 'Collected', align: 'right', sort: (r) => sumCollected(r), render: (r) => <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(sumCollected(r))}</Typography>, csv: (r) => sumCollected(r) },
  { key: 'lead', label: 'Lead', sort: (r) => (r.name || '').toLowerCase(), render: (r) => <Stack2 top={r.name} bottom={r.source} />, csv: (r) => r.name },
  { key: 'mobile', label: 'Lead mobile', sort: (r) => digits(r.mobile), render: (r) => <Mobile value={r.mobile} />, csv: (r) => r.mobile },
  { key: 'cro', label: 'CRO', sort: (r) => (first(r).cro || '').toLowerCase(), render: (r) => <Typography variant="body2">{first(r).cro || '-'}</Typography>, csv: (r) => first(r).cro },
];

const searchText = (r) => [r.name, digits(r.mobile), r.mobile, r.source, ...(r.admissions || []).flatMap((a) => [a.studentName, a.admnNo, a.course, a.program, a.cro, digits(a.studentMobile)])]
  .filter(Boolean).join(' ').toLowerCase();

const LeadDetailsDialog = ({ request, onClose }) => {
  const { kind, userName, category, month } = request;
  const columns = kind === 'admitted' ? ADMISSION_COLUMNS : LEAD_COLUMNS(kind);
  return (
    <DrilldownDialog
      url={leadDetailsUrl(request)}
      title={KIND_LABEL[kind]}
      chips={[userName, category, month.format('MMMM YYYY')]}
      columns={columns}
      searchText={searchText}
      searchLabel="Search these leads"
      searchPlaceholder={kind === 'admitted' ? 'Search student, admn no, course, mobile' : 'Search name or mobile'}
      emptyText="No leads."
      rowKey={(r) => r.leadId}
      csvName={`${KIND_LABEL[kind].replace(/ /g, '_')}_${(userName || 'person').replace(/\W+/g, '_')}_${month.format('YYYY-MM')}.csv`}
      onClose={onClose}
    />
  );
};

export default LeadDetailsDialog;
